// =============================================================================
// Shared form controls for the application funnel
// Mobile-first, premium styling consistent with the Revolution Travel brand.
// =============================================================================

import React from 'react';

const baseInput =
  'w-full rounded-lg border px-4 py-3 text-revolution-dark placeholder-gray-400 ' +
  'outline-none transition focus:ring-2';

function inputClasses(error) {
  return `${baseInput} ${
    error
      ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
      : 'border-gray-300 focus:border-revolution-blue focus:ring-blue-100'
  }`;
}

/** Single-line text / email / tel input. */
export function TextField({
  label,
  name,
  value,
  onChange,
  error,
  type = 'text',
  placeholder,
  autoComplete,
}) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-sm font-semibold text-revolution-navy">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(name, e.target.value)}
        className={inputClasses(error)}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/** Multi-line textarea with a live character hint. */
export function TextAreaField({ label, name, value, onChange, error, placeholder, rows = 4 }) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-sm font-semibold text-revolution-navy">
        {label}
      </label>
      <textarea
        id={name}
        name={name}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(name, e.target.value)}
        className={`${inputClasses(error)} resize-y`}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/** Accessible radio group rendered as selectable cards. */
export function RadioCards({ label, name, value, onChange, options, error, helpText }) {
  return (
    <fieldset>
      <legend className="block text-sm font-semibold text-revolution-navy">{label}</legend>
      {helpText && <p className="mt-1 text-sm text-gray-500">{helpText}</p>}
      <div className="mt-3 space-y-3">
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                selected
                  ? 'border-revolution-blue bg-blue-50 ring-1 ring-revolution-blue'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={selected}
                onChange={() => onChange(name, opt.value)}
                className="mt-0.5 h-4 w-4 accent-revolution-blue"
              />
              <span className="min-w-0">
                <span className="block font-medium text-revolution-navy">{opt.label}</span>
                {opt.hint && <span className="mt-0.5 block text-sm text-gray-500">{opt.hint}</span>}
              </span>
            </label>
          );
        })}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </fieldset>
  );
}

/** Segmented progress bar shown at the top of the application form. */
export function ProgressBar({ current, total, label }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-500">
        <span>
          Step {current} of {total}
        </span>
        {label && <span className="text-revolution-blue">{label}</span>}
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-revolution-orange transition-all duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
